import React, { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { styled } from '@mui/material/styles';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { IMsoolUnit } from '@services';
import { ChannelType } from '../../../../services/useChannels';

// Import unified channel logo components
import { ChannelLogo, ChannelLogoType } from '../ChannelLogos';
import { T_KEYS } from '../../constants';
import PlaceholderImage from '../UnitsMapping/PlaceholderImage';

const CardContainer = styled(Box)({
  backgroundColor: '#ffffff',
  borderRadius: '16px',
  border: '1px solid #e6e6e6',
  marginBottom: '24px',
  overflow: 'clip',
  boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.04)',
  padding: '1px',
});

const CardHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '16px 24px',
  backgroundColor: '#ffffff',
});

const HeaderRight = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  justifyContent: 'flex-start',
});

const HeaderTitle = styled(Typography)({
  fontSize: '16px',
  fontWeight: 600,
  color: '#1a1a1a',
  lineHeight: '24px',
});

const SearchInputContainer = styled(Box)({
  padding: '0 24px 16px 24px',
});

const StyledTextField = styled(TextField)({
  width: '100%',
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: '#F9FAFB',
    fontSize: '14px',
    '& fieldset': {
      borderColor: '#E5E7EB',
    },
    '&:hover fieldset': {
      borderColor: '#D1D5DB',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#5B3FFF',
      borderWidth: '1px',
    },
  },
  '& .MuiOutlinedInput-input': {
    padding: '12px 14px',
    '&::placeholder': {
      color: '#9CA3AF',
      opacity: 1,
    },
  },
});

const TableContainer = styled(Box)({
  width: '100%',
  padding: '16px',
});

const TableContent = styled(Box)({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'flex-start',
  width: '100%',
});

const TableColumn = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  flex: '1 0 0',
  minWidth: '0',
  minHeight: '1px',
});

const PropertyColumn = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  width: '600px',
});

const TableHeaderContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  justifyContent: 'center',
  paddingBottom: '4px',
  width: '100%',
});

const TableHeaderTextContainer = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'flex-start',
  padding: '8px 8px 4px 8px',
  width: '100%',
});

const TableHeaderText = styled(Typography)({
  fontSize: '12px',
  fontWeight: 700,
  color: '#37352f',
  fontFamily: '"Tajawal", sans-serif',
  textAlign: 'start',
  padding: '0 8px',
});

const Divider = styled(Box)({
  height: '1px',
  width: '100%',
  backgroundColor: '#e9e9e7',
});

const TableRowItem = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  justifyContent: 'center',
  height: '50px',
  padding: '14px 0',
  borderBottom: '1px solid #e9e9e7',
  '&:last-child': {
    borderBottom: 'none',
  },
});

const TableRowContent = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  padding: '2px 8px',
});

const PropertyRowContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  height: '50px',
  padding: '8px 0',
  width: '100%',
  borderBottom: '1px solid #e9e9e7',
  '&:last-child': {
    borderBottom: 'none',
  },
});

const PropertyRowContent = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  paddingInlineEnd: '16px',
});

const PropertyInfo = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
});

const PropertyName = styled(Typography)({
  fontSize: '14px',
  fontWeight: 700,
  color: '#331860',
  fontFamily: '"Tajawal", sans-serif',
  textAlign: 'start',
  whiteSpace: 'nowrap',
});

const ChannelsContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

// Note: Local ChannelLogo styled component removed - using unified ChannelLogo component instead

const EmptyState = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '40px',
  color: '#999999',
});

interface PropertiesTableProps {
  properties: IMsoolUnit[];
  isLoading?: boolean;
  onMenuOpen: (event: React.MouseEvent<HTMLButtonElement>, property: IMsoolUnit) => void;
  searchQuery?: string;
  onSearchChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  totalCount?: number;
}

const getChannelLogoType = (channelType: ChannelType): ChannelLogoType | null => {
  switch (channelType) {
    case ChannelType.Airbnb:
      return 'airbnb';
    case ChannelType.Booking:
      return 'booking';
    case ChannelType.Gathern:
      return 'gathern';
    default:
      return null;
  }
};

const renderChannelLogo = (channelType: ChannelType, index: number, isActive: boolean = true) => {
  const logoType = getChannelLogoType(channelType);
  if (!logoType) return null;
  
  return (
    <ChannelLogo 
      key={`${logoType}-${index}`} 
      channel={logoType} 
      size="sm" 
      rounded 
      disabled={!isActive}
    />
  );
};

const PropertiesTable: React.FC<PropertiesTableProps> = memo(({ 
  properties, 
  isLoading, 
  onMenuOpen,
  searchQuery = '',
  onSearchChange,
  totalCount,
}) => {
  const { t } = useTranslation();

  const hasNoResults = !properties || properties.length === 0;
  const isSearchActive = searchQuery && searchQuery.trim().length > 0;

  return (
    <CardContainer>
      <CardHeader>
        <HeaderRight>
          <HeaderTitle>
            {t(T_KEYS.PROPERTIES_LIST)}
          </HeaderTitle>
        </HeaderRight>
        <Box>
          <Typography sx={{ fontSize: '14px', color: '#7a7a7a', lineHeight: '20px' }}>
            {totalCount ?? properties.length} {t(T_KEYS.UNITS_MAPPING_PROPERTY)}
          </Typography>
        </Box>
      </CardHeader>

      {/* Search Input */}
      {onSearchChange && (
        <SearchInputContainer>
          <StyledTextField
            placeholder={t(T_KEYS.SEARCH_PROPERTIES) || 'البحث عن عقار...'}
            value={searchQuery}
            onChange={onSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#9CA3AF', fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
          />
        </SearchInputContainer>
      )}

      {/* Show "no search results" message when search is active but no results */}
      {hasNoResults && isSearchActive && (
        <EmptyState>
          <Typography>
            {t(T_KEYS.NO_SEARCH_RESULTS) || 'لا توجد نتائج للبحث'}
          </Typography>
        </EmptyState>
      )}

      {/* Show table content only when there are results */}
      {!hasNoResults && (
      <TableContainer>
        <TableContent>
          {/* Property Column */}
          <PropertyColumn>
            <TableHeaderContainer>
              <TableHeaderTextContainer>
                <TableHeaderText>
                  {t(T_KEYS.PROPERTY)}
                </TableHeaderText>
              </TableHeaderTextContainer>
              <Divider />
            </TableHeaderContainer>
            {properties.map((property) => (
              <PropertyRowContainer key={`property-${property.propertyId}`}>
                <PropertyRowContent>
                  <PropertyInfo>
                    <PlaceholderImage
                      src={property.imageUrl || property.channels?.[0]?.listingImage}
                      alt={property.propertyName ?? ''}
                      fallbackText={property.propertyName?.charAt(0)}
                    />
                    <PropertyName>{property.propertyName}</PropertyName>
                  </PropertyInfo>
                </PropertyRowContent>
              </PropertyRowContainer>
            ))}
          </PropertyColumn>

          {/* Address Column */}
          <TableColumn>
            <TableHeaderContainer>
              <TableHeaderTextContainer>
                <TableHeaderText>
                  {t(T_KEYS.ADDRESS)}
                </TableHeaderText>
              </TableHeaderTextContainer>
              <Divider />
            </TableHeaderContainer>
            {properties.map((property) => (
              <TableRowItem key={`address-${property.propertyId}`}>
                <TableRowContent>
                  <Typography sx={{ fontSize: '14px', color: '#697586' }}>
                    -
                  </Typography>
                </TableRowContent>
              </TableRowItem>
            ))}
          </TableColumn>

          {/* Channels Column */}
          <TableColumn>
            <TableHeaderContainer>
              <TableHeaderTextContainer>
                <TableHeaderText>
                  {t(T_KEYS.CHANNELS)}
                </TableHeaderText>
              </TableHeaderTextContainer>
              <Divider />
            </TableHeaderContainer>
            {properties.map((property) => (
              <TableRowItem key={`channels-${property.propertyId}`}>
                <TableRowContent>
                  <ChannelsContainer>
                    {property.channels?.map((linkedUnit, index) => 
                      renderChannelLogo(linkedUnit.channelType, index, linkedUnit.status)
                    )}
                  </ChannelsContainer>
                </TableRowContent>
              </TableRowItem>
            ))}
          </TableColumn>

          {/* Action Column */}
          <TableColumn>
            <TableHeaderContainer>
              <TableHeaderTextContainer>
                <TableHeaderText>
                  {t(T_KEYS.UNITS_MAPPING_ACTIONS)}
                </TableHeaderText>
              </TableHeaderTextContainer>
              <Divider />
            </TableHeaderContainer>
            {properties.map((property) => (
              <TableRowItem key={`action-${property.propertyId}`}>
                <TableRowContent>
                  <IconButton 
                    size="small" 
                    onClick={(e) => onMenuOpen(e, property)}
                  >
                    <MoreHorizIcon />
                  </IconButton>
                </TableRowContent>
              </TableRowItem>
            ))}
          </TableColumn>
        </TableContent>
      </TableContainer>
      )}
    </CardContainer>
  );
});

PropertiesTable.displayName = 'PropertiesTable';

export default PropertiesTable;
